'use client';

import { FC, useEffect, useRef } from 'react';
import Link from 'next/link';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { PrismicRichText } from '@prismicio/react';
import { asText, Content } from '@prismicio/client';
import {
  CaseStudyDocument,
  TestimonialCarousel2SliceFromMarkedCases,
} from '../../../../prismicio-types';
import { brandOf, prepareCaseStudies } from '../shared/prepareCaseStudies';

interface FromMarkedCasesProps {
  slice: TestimonialCarousel2SliceFromMarkedCases;
  caseStudies: CaseStudyDocument[];
}

/**
 * "From marked case studies" variation of CaseStudiesCarousel.
 *
 * Data flow:
 *   - The server `index.tsx` resolves the `case_study` documents (editor picks
 *     first, otherwise all of them) and passes them in via `caseStudies`.
 *   - Before rendering, `prepareCaseStudies` dedups by brand (keeping the most
 *     complete of any duplicates) and drops cards that aren't render-ready, so
 *     no empty/half-filled or repeated client cards appear.
 *   - Each card links to the case study detail page.
 *
 * Removing the filter / search UI is intentional (per Linda) — a single-item
 * filter result was the original UX problem with the older variation.
 */
const FromMarkedCases: FC<FromMarkedCasesProps> = ({ slice, caseStudies }) => {
  const titleRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Reveal on mount, NOT on scroll. The previous IntersectionObserver left
    // the grid at `opacity-0` until it scrolled into view, so on /clients the
    // cards were invisible until the user scrolled. Revealing on the next frame
    // (after the opacity-0 state has painted) keeps the fade-in animation while
    // guaranteeing the cards are always visible.
    const reveal = (el: HTMLDivElement | null, delay: string) => {
      if (!el) {
        return;
      }
      el.style.transitionDelay = delay;
      el.classList.add('animate-fade-in');
      el.classList.remove('opacity-0', 'translate-y-8');
    };
    const id = requestAnimationFrame(() => {
      reveal(titleRef.current, '0ms');
      reveal(gridRef.current, '150ms');
    });
    return () => cancelAnimationFrame(id);
  }, []);

  const { title, description, primary_action } = slice.primary;
  // Dedup repeated clients (keep the richest) and drop incomplete cards.
  const visibleCaseStudies = prepareCaseStudies(caseStudies);

  return (
    <section
      // The variation type narrows away `slice_type` (it lives on the
      // SharedSlice wrapper, not the variation), so we hardcode it here for
      // the data attribute. Matches the API ID in model.json.
      data-slice-type="testimonial_carousel_2"
      data-slice-variation={slice.variation}
      className="bg-white py-20 text-[#041118]"
    >
      <div className="container mx-auto px-5 md:px-10 lg:px-[200px]">
        <div
          ref={titleRef}
          className="mb-12 text-center opacity-0 translate-y-8 transition-all duration-700 ease-out"
        >
          <div className="text-[30px] font-medium leading-[1.2] capitalize">
            <PrismicRichText field={title} />
          </div>
          {asText(description) && (
            <div className="mt-4 mx-auto max-w-2xl text-base text-[#041118]/70">
              <PrismicRichText field={description} />
            </div>
          )}
        </div>

        <div
          ref={gridRef}
          // Cards are pinned at 344×469 (Figma). `justify-items-center`
          // centers them in their grid cell so the design looks identical
          // even when the column is wider than 344px.
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center opacity-0 translate-y-8 transition-all duration-700 ease-out"
        >
          {visibleCaseStudies.map((cs) => (
            <CaseStudyCard key={cs.id} caseStudy={cs} />
          ))}
        </div>

        {primary_action && primary_action.link_type !== 'Any' && (
          <div className="mt-12 flex justify-center">
            <PrismicNextLink
              field={primary_action}
              className="inline-flex items-center px-8 py-4 font-semibold transition-opacity duration-300 hover:opacity-90"
              style={{
                borderRadius: '100px',
                background: '#041118',
                color: 'white',
              }}
            >
              {(primary_action as { text?: string }).text || 'View all'}
            </PrismicNextLink>
          </div>
        )}
      </div>
    </section>
  );
};

interface CaseStudyCardProps {
  caseStudy: CaseStudyDocument;
}

const CaseStudyCard: FC<CaseStudyCardProps> = ({ caseStudy }) => {
  const { industry, image, description } =
    caseStudy.data as Content.CaseStudyDocumentData;
  const href = caseStudy.url ?? '#';
  // Same brand resolution as dedup/completeness (client → name, prefix cleaned).
  const brand = brandOf(caseStudy);

  return (
    <Link
      href={href}
      // Lock to the exact Figma spec (344×469) at every viewport and
      // center inside the grid cell. The fixed dimensions are what keep
      // padding, typography and image area looking identical regardless
      // of screen size — the cards never stretch to fill the column.
      className="group relative mx-auto flex h-[469px] w-full max-w-[344px] cursor-pointer flex-col overflow-hidden border border-[#CCC] text-white shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] transition-shadow hover:shadow-[0_8px_32px_rgba(0,0,0,0.18)]"
      style={{
        borderRadius: '16px',
        background:
          'linear-gradient(180deg, #041118 46.68%, rgba(0, 0, 0, 0.00) 100%), linear-gradient(270deg, #041118 0%, #13094A 50%, #041118 100%)',
      }}
    >
      {/* Top: image with CASE STUDY pill overlay */}
      <div className="relative h-[200px] w-full shrink-0 overflow-hidden">
        <PrismicNextImage
          field={image}
          className="h-full w-full object-cover"
          alt=""
          fallbackAlt=""
        />
        <span className="absolute left-4 top-4 inline-flex items-center rounded-[4px] bg-black/40 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-white backdrop-blur-sm">
          Case Study
        </span>
      </div>

      {/* Bottom: industry, brand name, description, CTA.
          `min-h-0` lets the flex child shrink so the description's
          `line-clamp-5` can actually truncate inside the bounded card. */}
      <div className="flex min-h-0 flex-1 flex-col gap-3 px-6 py-5">
        {industry && (
          <div className="flex items-center gap-2">
            <span aria-hidden className="block h-3 w-[2px] bg-[#00F557]" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-white/80">
              {industry}
            </span>
          </div>
        )}

        <div className="font-serif text-[26px] font-medium leading-[1.1] text-white line-clamp-2">
          {brand}
        </div>

        {asText(description) && (
          <div className="text-[13px] leading-[1.55] text-white/80 line-clamp-5 [&_p]:m-0">
            <PrismicRichText
              field={description}
              components={{
                paragraph: ({ children }) => (
                  <p>
                    <span aria-hidden className="text-[#00F557]">
                      →{' '}
                    </span>
                    {children}
                  </p>
                ),
              }}
            />
          </div>
        )}

        <span className="mt-auto pt-2 text-[13px] font-medium text-white underline underline-offset-4 transition-opacity group-hover:opacity-80">
          View case study
        </span>
      </div>
    </Link>
  );
};

export default FromMarkedCases;
